import { useContext, useMemo } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import { useFeatureFlag } from './useFeatureFlag';

/**
 * Hook to check if user has permission to view a card/component
 * 
 * @param permissions - Array of allowed role constants (e.g., [ROLE_ADMIN, ROLE_MANAGER])
 * @param featureFlagKey - Optional feature flag key to check
 * @returns boolean indicating if user can view the card/component
 */
export const useCardPermission = (
  permissions: number[],
  featureFlagKey?: string
): boolean => {
  const { user } = useContext(AuthContext);

  const isFeatureEnabled = useFeatureFlag(featureFlagKey || '');

  return useMemo(() => {
    if (!user) {
      return false;
    }

    if (featureFlagKey && !isFeatureEnabled) {
      return false;
    }

    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.includes(user.role as number);
  }, [user, user?.role, permissions, featureFlagKey, isFeatureEnabled]);
};